import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  orderBy,
  startAfter,
  limit,
} from 'firebase/firestore';
import { db, CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET } from './firebase';

/** Fetch a shipment by tracking number */
export async function getShipmentByTracking(trackingNumber) {
  const q = query(
    collection(db, 'shipments'),
    where('trackingNumber', '==', trackingNumber.trim())
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

/** Fetch clearance fee for a shipment */
export async function getClearanceFee(shipmentId) {
  const q = query(
    collection(db, 'clearanceFees'),
    where('shipmentId', '==', shipmentId)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

/** Fetch all active payment wallets */
export async function getActiveWallets() {
  const snap = await getDocs(collection(db, 'paymentWallets'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(w => w.isActive);
}

/** Store a payment confirmation from the user */
export async function submitPaymentConfirmation(data) {
  await addDoc(collection(db, 'paymentConfirmations'), {
    ...data,
    status: 'pending_verification',
    submittedAt: serverTimestamp(),
  });
}

export const APPLICATIONS_COL = 'jobApplications';
export const PAGE_SIZE = 10;
 
// ── Upload file to Cloudinary ──────────────────────────────────
export async function uploadToCloudinary(file) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('resource_type', 'auto'); // supports pdf, doc, images
 
  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
    { method: 'POST', body: formData }
  );
  if (!res.ok) throw new Error('Cloudinary upload failed');
  const data = await res.json();
  return { url: data.secure_url, publicId: data.public_id, format: data.format };
}
 
// ── Submit a job application ───────────────────────────────────
export async function submitApplication(data) {
  const ref = await addDoc(collection(db, APPLICATIONS_COL), {
    ...data,
    status: 'pending',
    submittedAt: serverTimestamp(),
  });
  return ref.id;
}
 
// ── Paginated fetch for admin ──────────────────────────────────
export async function getApplications(lastVisible = null) {
  const q = lastVisible
    ? query(collection(db, APPLICATIONS_COL), orderBy('submittedAt', 'desc'), startAfter(lastVisible), limit(PAGE_SIZE))
    : query(collection(db, APPLICATIONS_COL), orderBy('submittedAt', 'desc'), limit(PAGE_SIZE));
 
  const snap = await getDocs(q);
  return {
    docs: snap.docs.map(d => ({ id: d.id, ...d.data() })),
    lastVisible: snap.docs[snap.docs.length - 1] ?? null,
    hasMore: snap.docs.length === PAGE_SIZE,
  };
}
 
// ── Update application status ──────────────────────────────────
export async function updateApplicationStatus(id, status) {
  await updateDoc(doc(db, APPLICATIONS_COL, id), { status });
}
 